import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { apiClient } from "@/lib/api-client";
import { useDateFormat } from "@/utils/date-formatter";

interface TicketHistory {
  id: string;
  ticketId: string;
  userId: string;
  changeType: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  description?: string;
  createdAt: string;
  userName?: string | null; // User who made the change (for backward compatibility, may be null)
}

interface TicketHistoryProps {
  ticketId: string;
}

export default function TicketHistory({ ticketId }: TicketHistoryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation(['components.ticketHistory']);
  const { formatDateTime } = useDateFormat();

  // Lade die Historie für das aktuelle Ticket - OPTIMIERT für MEGA SPEED
  const { data: history = [], isLoading } = useQuery<TicketHistory[]>({
    queryKey: [`/api/tickets/${ticketId}/history`],
    enabled: !!ticketId, // Nur laden wenn ticketId vorhanden ist
    staleTime: 30 * 1000, // 30 Sekunden ultraschnell Cache
    gcTime: 60 * 1000, // 1 Minute im Memory
    refetchOnWindowFocus: false, // Nicht bei Fenster-Fokus neu laden
    queryFn: async () => {
      const response = await apiClient.getTicketHistory(ticketId, { limit: 100, offset: 0 });
      return response.items as TicketHistory[];
    },
  });

  // Helper function um Änderungstypen zu übersetzen und zu formatieren
  const getChangeDescription = (historyEntry: TicketHistory) => {
    const { changeType, fieldName, oldValue, newValue, description } = historyEntry;
    
    // Wenn eine Beschreibung vorhanden ist, verwende sie (includes weather info for photo_added)
    if (description) {
      return description;
    }
    
    // Ansonsten erstelle automatisch eine Beschreibung
    switch (changeType) {
      case 'created':
        return t('components.ticketHistory:entry.created');
      case 'photo_added':
        // Description already includes weather info from backend
        return description || t('components.ticketHistory:entry.photoAdded', { defaultValue: 'Photo added' });
      case 'updated':
        if (fieldName === 'status') {
          return t('components.ticketHistory:entry.statusChanged', { old: oldValue || 'New', new: newValue });
        }
        if (fieldName === 'assigneeName') {
          return t('components.ticketHistory:entry.updated', { field: t('components.ticketHistory:entry.assignee'), old: oldValue || t('components.ticketHistory:unknown'), new: newValue || t('components.ticketHistory:unknown') });
        }
        if (fieldName === 'priority') {
          return t('components.ticketHistory:entry.updated', { field: t('components.ticketHistory:entry.priority'), old: oldValue || '-', new: newValue || '-' });
        }
        if (fieldName === 'progress') {
          return t('components.ticketHistory:entry.updated', { field: t('components.ticketHistory:entry.progress'), old: `${oldValue || '0'}%`, new: `${newValue}%` });
        }
        return t('components.ticketHistory:entry.updated', { field: fieldName, old: oldValue, new: newValue });
      case 'status_changed':
        return t('components.ticketHistory:entry.statusChanged', { old: oldValue, new: newValue });
      case 'assigned':
        return t('components.ticketHistory:entry.assigned', { new: newValue });
      case 'unassigned':
        return t('components.ticketHistory:entry.unassigned');
      default:
        return `${changeType}: ${fieldName}`;
    }
  };

  if (!ticketId) {
    return null;
  }

  // console.log('🎯 HISTORIE-KOMPONENTE: Wird gerendert für Ticket:', ticketId);
  // console.log('📊 HISTORIE-DATEN:', { history, isLoading, enabled: !!ticketId });

  const sortedHistory = [...history].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  const latestEntry = sortedHistory[0];

  const formatHistoryTimestamp = (createdAt: string | undefined) => {
    if (!createdAt) {
      return t('components.ticketHistory:unknown');
    }
    return formatDateTime(createdAt);
  };

  const renderHistoryEntry = (entry: TicketHistory, index: number) => (
    <div
      key={entry.id}
      className="border-l-2 border-slate-200 pl-3 py-0.5 text-xs"
      data-testid={`history-entry-${index}`}
    >
      <div className="text-slate-700 font-medium">
        <span className="text-slate-500 text-xs mr-2">
          {formatHistoryTimestamp(entry.createdAt)}
        </span>
        {getChangeDescription(entry)}
        <span className="text-slate-400 text-xs ml-2">
          • {entry.userName || '-'}
        </span>
      </div>
    </div>
  );

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="space-y-2 relative">
        {sortedHistory.length > 0 && (
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-0 -top-3 h-5 w-5 p-0 text-slate-600 hover:text-slate-800 hover:bg-transparent"
              data-testid="button-toggle-ticket-history"
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
        )}

        {isLoading ? (
          <div className="text-xs text-slate-400 py-2 px-3">
            {t('components.ticketHistory:loading')}
          </div>
        ) : sortedHistory.length === 0 ? (
          <div className="text-xs text-slate-400 py-2 px-3">
            {t('components.ticketHistory:empty')}
          </div>
        ) : (
          <>
            {!isExpanded && latestEntry ? (
              <div className="space-y-1 py-1 pr-6">
                {renderHistoryEntry(latestEntry, 0)}
              </div>
            ) : null}

            <CollapsibleContent className="space-y-1">
              <div className="max-h-48 overflow-y-auto space-y-0 py-1 pr-2">
                {sortedHistory.map((entry, index) => renderHistoryEntry(entry, index))}
              </div>
            </CollapsibleContent>
          </>
        )}
      </div>
    </Collapsible>
  );
}
